// Organic templates (ported from v1 to the renderdoc method).
import { aestheticLine1 } from './aesthetic-line-1.js';
import { AUTHORED_TEMPLATES } from './authored/index.js';
import { authority1 } from './authority-1.js';
import { authority2 } from './authority-2.js';
import { authority3 } from './authority-3.js';
import { beforeAfter1 } from './before-after-1.js';
import { beforeAfter2 } from './before-after-2.js';
import { beforeAfter3 } from './before-after-3.js';
import { captionTease1 } from './caption-tease-1.js';
import { clientQuestion1 } from './client-question-1.js';
import { curiosityHook1 } from './curiosity-hook-1.js';
import { educational1 } from './educational-1.js';
import { educational2 } from './educational-2.js';
import { educational3 } from './educational-3.js';
import { fadeBenefits1 } from './fade-benefits-1.js';
import { highlightCaption1 } from './highlight-caption-1.js';
import { improves1 } from './improves-1.js';
import { insOuts1 } from './ins-outs-1.js';
import { mythFact1 } from './myth-fact-1.js';
import { numberedList1 } from './numbered-list-1.js';
import { offerSquare1 } from './offer-square-1.js';
import { poll1 } from './poll-1.js';
import { priceReveal1 } from './price-reveal-1.js';
import { questionCta1 } from './question-cta-1.js';
import { stepTimer1 } from './step-timer-1.js';
import { timeProgress1 } from './time-progress-1.js';
export const BUILT_IN_TEMPLATES = [
    educational1,
    educational2,
    educational3,
    authority1,
    authority2,
    authority3,
    beforeAfter1,
    beforeAfter2,
    beforeAfter3,
    offerSquare1,
    // Organic formats.
    captionTease1,
    fadeBenefits1,
    aestheticLine1,
    numberedList1,
    insOuts1,
    questionCta1,
    improves1,
    highlightCaption1,
    curiosityHook1,
    stepTimer1,
    timeProgress1,
    clientQuestion1,
    poll1,
    mythFact1,
    priceReveal1,
];
/**
 * The registry resolves BOTH pools by id.
 *
 * `BUILT_IN_TEMPLATES` stays exactly as it was — every existing caller that
 * iterates it (pickers, tests, the plannable-format guards) keeps its current
 * behaviour, and adding 14 authored documents does not silently widen any of
 * them. What changes is only that `getTemplateDocById` can now resolve an
 * authored id, which is what a video row carrying `variationId: 'reel-2'`
 * needs in order to render as itself.
 *
 * Being resolvable is deliberately NOT being selectable. Nothing generates
 * these until the product catalogue (CONTENT_IDEA_TEMPLATES in
 * @borradh-workspace/features) carries a matching entry.
 */
const registry = new Map(BUILT_IN_TEMPLATES.map((t) => [t.id, t]));
for (const doc of AUTHORED_TEMPLATES) {
    // A collision would make which document renders depend on load order, and
    // the symptom — a video rendering as a DIFFERENT format — looks like a
    // planner bug from every angle except this one.
    const clash = registry.get(doc.id);
    if (clash) {
        throw new Error(`authored template "${doc.id}" collides with a built-in of the same id`);
    }
    registry.set(doc.id, doc);
}
export function getTemplateDocById(id) {
    return registry.get(id);
}
/**
 * Make a doc that is NOT in `BUILT_IN_TEMPLATES` resolvable by id.
 *
 * `BUILT_IN_TEMPLATES` is a static code registry of hand-written docs, and the
 * Map above is built from it once at module load — so pushing onto the array
 * later does nothing. A DERIVED template (one mapped from an analysed reel) is
 * data, not code: it arrives at runtime, per-org, versioned by a row rather
 * than by git, and has no way into that Map at all.
 *
 * This is the minimum seam that makes such a doc renderable. It is deliberately
 * NOT the DB-backed doc source that shipping derived templates needs (see
 * reel-format-derivation.md N6) — there is no `origin: 'authored' | 'derived'`
 * discriminator here, so planners cannot yet be told which pool to draw from.
 * Until that exists, this is for harnesses and tests, and a derived doc must
 * not be registered on a request path where a planner could pick it up.
 */
export function registerTemplateDoc(doc) {
    registry.set(doc.id, doc);
}
/**
 * Re-exported here rather than only from `./authored/index.js` so that
 * consumers reach both pools through the registry module, and so
 * `export * from './template-registry.js'` in the package index carries it.
 */
export { AUTHORED_TEMPLATES } from './authored/index.js';
//# sourceMappingURL=template-registry.js.map